import React from "react";
import { CompetitionStage, Match, TournamentEvent, TournamentParticipant } from "../../../types";
import { formatClock, formatMinutes, minutesOfDay } from "../../../utils/datetime";
import { MatchCard } from "../../../components/common/tournament-event/MatchCard";
import {
  ConflictKind,
  EventColumn,
  MIN_CARD_HEIGHT,
  SlotLayout,
  columnWidthPx,
  conflictBadgeInfo,
} from "./matchLayout";

interface CalendarTimeGridProps {
  eventColumns: EventColumn[];
  slotLayout: SlotLayout;
  conflictsByMatchId: Map<number, Set<ConflictKind>>;
  participantsById: Map<number, TournamentParticipant>;
  stagesById: Map<number, CompetitionStage>;
  eventsById: Map<number, TournamentEvent>;
  canManage: boolean;
  onSelectMatch: (match: Match) => void;
}

// Vista time-grid a slot: una colonna per Torneo, righe per orario di inizio,
// card posizionate in modo assoluto (con corsie affiancate per i match in
// contemporanea). Scroll orizzontale su schermi stretti.
export const CalendarTimeGrid: React.FC<CalendarTimeGridProps> = ({
  eventColumns,
  slotLayout,
  conflictsByMatchId,
  participantsById,
  stagesById,
  eventsById,
  canManage,
  onSelectMatch,
}) => {
  const gridHeight = slotLayout.totalHeight;
  const gridTemplateColumns = `64px ${
    eventColumns.length
      ? eventColumns.map((column) => `minmax(${columnWidthPx(column.lanes)}px, 1fr)`).join(" ")
      : "minmax(220px, 1fr)"
  }`;
  const gridMinWidth =
    64 +
    (eventColumns.length
      ? eventColumns.reduce((sum, column) => sum + columnWidthPx(column.lanes), 0)
      : 220);

  return (
    <div className="overflow-x-auto rounded border border-gray-300 bg-white shadow-sm">
      <div className="min-w-full" style={{ minWidth: `${gridMinWidth}px` }}>
        <div className="grid border-b border-gray-300" style={{ gridTemplateColumns }}>
          <div className="bg-gray-50" />
          {eventColumns.length ? (
            eventColumns.map(({ eventId, event, matches: eventMatches }) => (
              <div key={eventId} className="border-l border-gray-300 p-2 text-center">
                <div className="truncate text-sm font-bold text-gray-900">
                  {event?.tournament.name ?? `Evento #${eventId}`}
                </div>
                <div className="truncate text-xs text-gray-500">
                  {event?.location?.name ?? "Sede TBD"} · {eventMatches.length} match
                </div>
              </div>
            ))
          ) : (
            <div className="border-l border-gray-300 p-2 text-center text-sm text-gray-500">
              Nessun evento
            </div>
          )}
        </div>

        <div className="grid" style={{ gridTemplateColumns }}>
          <div className="relative bg-gray-50" style={{ height: gridHeight }}>
            {slotLayout.slots.map((slot) => (
              <div
                key={slot.minutes}
                className="absolute left-0 right-0 -translate-y-2 pr-2 text-right text-xs font-medium text-gray-600"
                style={{ top: slot.top }}
              >
                {formatMinutes(slot.minutes)}
              </div>
            ))}
          </div>

          {eventColumns.length ? (
            eventColumns.map(({ eventId, matches: eventMatches }) => (
              <div key={eventId} className="relative border-l border-gray-300" style={{ height: gridHeight }}>
                {slotLayout.slots.map((slot) => (
                  <div
                    key={slot.minutes}
                    className="absolute left-0 right-0 border-t border-gray-100"
                    style={{ top: slot.top }}
                  />
                ))}
                {eventMatches.map((item) => {
                  const match = item.match;
                  const stage = stagesById.get(match.stage);
                  const event = stage ? eventsById.get(stage.event) : null;
                  const player1 = match.player1 ? participantsById.get(match.player1)?.name : null;
                  const player2 = match.player2 ? participantsById.get(match.player2)?.name : null;
                  const top = slotLayout.yOf(minutesOfDay(item.start));
                  const height = Math.max(MIN_CARD_HEIGHT, slotLayout.yOf(slotLayout.endMinutesOf(item)) - top);
                  const conflictKinds = conflictsByMatchId.get(match.id);
                  const conflicting = !!conflictKinds;
                  const conflictBadge = conflictKinds ? conflictBadgeInfo(conflictKinds) : null;
                  const laneWidth = 100 / item.lanes;

                  return (
                    <MatchCard
                      key={match.id}
                      match={match}
                      player1Label={player1 ?? "TBD"}
                      player2Label={player2 ?? "TBD"}
                      timeLabel={`${formatClock(item.start)} - ${formatClock(item.end)}`}
                      subtitle={`${event?.tournament.name ?? "Torneo"} · ${stage?.name ?? `Stage #${match.stage}`}`}
                      extraBadge={
                        conflictBadge ? (
                          <span title={conflictBadge.title} className="rounded bg-red-100 px-1 text-red-700">
                            {conflictBadge.label}
                          </span>
                        ) : null
                      }
                      onClick={canManage ? () => onSelectMatch(match) : undefined}
                      style={{
                        top,
                        height,
                        left: `calc(${item.lane * laneWidth}% + 2px)`,
                        width: `calc(${laneWidth}% - 4px)`,
                      }}
                      className={`absolute overflow-hidden p-2 text-xs shadow-sm ${
                        conflicting
                          ? "z-10 border-red-400 bg-red-50 ring-1 ring-red-200"
                          : "border-gray-300 bg-white hover:border-blue-300"
                      } ${canManage ? "cursor-pointer hover:bg-blue-50" : "cursor-default"}`}
                    />
                  );
                })}
              </div>
            ))
          ) : (
            <div className="relative border-l border-gray-300" style={{ height: gridHeight }}>
              {slotLayout.slots.map((slot) => (
                <div
                  key={slot.minutes}
                  className="absolute left-0 right-0 border-t border-gray-100"
                  style={{ top: slot.top }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
